import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Dropdown, Spinner } from 'react-bootstrap';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { GoDotFill } from 'react-icons/go';
import { PiChecksBold } from 'react-icons/pi';
import { BiSolidSmile } from 'react-icons/bi';
import { FaPlay, FaPause } from 'react-icons/fa';
import { MdAttachFile, MdDoNotDisturbAlt, MdVerified } from 'react-icons/md';
import { GiOldMicrophone } from 'react-icons/gi';
import { RiSendPlaneFill } from 'react-icons/ri';
import { IoClose, IoVideocam } from 'react-icons/io5';
import { HiCamera } from 'react-icons/hi2';
import { push, ref, set, update, get, onValue, remove } from 'firebase/database';
import { rtdb } from 'customhooks/firebase';
import { decrypt, encrypt } from 'customhooks/helper';
import dayjs from 'utils/dayjs';
import { getCandidateBlockState } from 'utils/blocking';
import { encryptId } from 'utils/urlId';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { apiService } from 'services/apiService';
import NoProfile from 'assets/images/NoProfile.png';
import FavLogo from 'assets/images/fav_logo.png';
import 'assets/css/chats.css';
import { Flag } from "lucide-react";
import { RootState } from 'store';
import { useSelector } from 'react-redux';
import './ChatBoard.css';

import { lazyWithRetry } from 'utils/lazyWithRetry';

type EmojiClickData = import('emoji-picker-react').EmojiClickData;
const EmojiPicker = lazyWithRetry(() => import('emoji-picker-react'));

const MESSAGE_PREVIEW_CHAR_LIMIT = 500;

export interface ChatBoardProps {
  // who we are chatting with
  otherUserId: string;
  otherUserName: string;
  otherUserImage?: string;
  otherUserOnline?: boolean;
  matchId?: string;
  matchStatus?: boolean;
  blockStatus?: { hasBlocked?: string; has_blocked?: string; isBlocked?: string; is_blocked?: string; isBloacked?: string; lastReportedDate?: string | null };
  // current logged-in user
  currentUserId: string;
  currentUserName: string;
  currentUserImage?: string;
  currentUserEmail?: string;
  /**
   * When provided, this chat is scoped to a specific job.
   * Firebase paths become: ${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/chat/job_{jobId}/{userId}-{otherUserId}
   * Conversations from different jobs are fully isolated.
   */
  jobId?: string;
  jobTitle?: string;
  // optional callbacks
  onFeedback?: () => void;
  onVideoCall?: () => void;
  onUnmatch?: () => void;
  onBlock?: () => void;
  onReport?: () => void;
  onConnect?: () => void;
  onDeleteChat?: () => void;
  connected?: boolean | number | string;
}

interface ChatMessage {
  chat_id: string;
  sender_id: string;
  sender_name: string;
  receiver_id: string;
  text: string;
  decryptMsg?: string;
  replyMsgDecrypt?: string;
  decryptPicUrl?: string;
  time: string;
  timestamp: string;
  type: string; // 'text' | 'image' | 'audio' | 'file' | 'delete'
  status: string;
  isReply?: boolean;
  idEdited?: boolean;
  parentMessage?: any;
  pic_url?: string;   // used for both sender avatar AND image/audio/file URL (matches app structure)
  duration?: string;  // audio duration in "MM:SS" format (matches app)
  [key: string]: any;
  connected?: boolean | number;
}

const VoiceAudioPlayer: React.FC<{ url: string; duration?: string; isSelf?: boolean }> = ({ url, duration, isSelf }) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(0);
  const [hasError, setHasError] = useState(false);

  const resolvedUrl = useMemo(() => {
    if (!url) return '';
    let clean = url.trim();
    if (clean.startsWith('http://')) {
      clean = clean.replace('http://', 'https://');
    }
    return clean;
  }, [url]);

  const togglePlay = () => {
    if (!audioRef.current || !resolvedUrl) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      setHasError(false);
      audioRef.current.play().catch((err) => {
        console.error('[VoiceAudioPlayer] Audio playback error:', err);
        setHasError(true);
      });
    }
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setTotalDuration(audioRef.current.duration || 0);
      setHasError(false);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = Number(e.target.value);
    setCurrentTime(val);
    if (audioRef.current) {
      audioRef.current.currentTime = val;
    }
  };

  const formatTime = (secs: number) => {
    if (isNaN(secs) || secs < 0 || !isFinite(secs)) return '00:00';
    const m = Math.floor(secs / 60).toString().padStart(2, '0');
    const s = Math.floor(secs % 60).toString().padStart(2, '0');
    return `${m}:${s}`;
  };

  const displayDuration = duration || (totalDuration > 0 ? formatTime(totalDuration) : '00:00');

  return (
    <div style={{
      display: 'flex',
      alignItems: 'center',
      gap: 10,
      padding: '8px 12px',
      borderRadius: 20,
      background: isSelf ? 'rgba(255, 255, 255, 0.2)' : '#F1F5F9',
      border: isSelf ? '1px solid rgba(255, 255, 255, 0.35)' : '1px solid #E2E8F0',
      minWidth: 230,
      maxWidth: 300,
      color: isSelf ? '#FFFFFF' : '#1E293B',
    }}>
      <audio
        ref={audioRef}
        src={resolvedUrl}
        preload="metadata"
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        onTimeUpdate={handleTimeUpdate}
        onLoadedMetadata={handleLoadedMetadata}
        onEnded={handleEnded}
        onError={() => setHasError(true)}
      />

      <button
        type="button"
        onClick={togglePlay}
        disabled={!resolvedUrl}
        style={{
          width: 34,
          height: 34,
          borderRadius: '50%',
          border: 'none',
          background: isSelf ? '#FFFFFF' : '#457EFF',
          color: isSelf ? '#457EFF' : '#FFFFFF',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: resolvedUrl ? 'pointer' : 'not-allowed',
          flexShrink: 0,
          boxShadow: '0 2px 5px rgba(0,0,0,0.15)',
        }}
      >
        {isPlaying ? <FaPause size={12} /> : <FaPlay size={12} style={{ marginLeft: 2 }} />}
      </button>

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <input
          type="range"
          min={0}
          max={totalDuration || 100}
          value={currentTime}
          onChange={handleSeek}
          disabled={!resolvedUrl || totalDuration === 0}
          style={{
            width: '100%',
            height: 4,
            accentColor: isSelf ? '#FFFFFF' : '#457EFF',
            cursor: 'pointer',
          }}
        />
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, opacity: 0.9, fontWeight: 500 }}>
          <span>{formatTime(currentTime)}</span>
          <span>{displayDuration}</span>
        </div>
      </div>

      {hasError && resolvedUrl && (
        <a
          href={resolvedUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Open audio file directly"
          style={{ color: isSelf ? '#FFE4E6' : '#EF4444', fontSize: 11, textDecoration: 'underline' }}
        >
          Open ↗
        </a>
      )}
    </div>
  );
};

const ChatBoard: React.FC<ChatBoardProps> = ({
  otherUserId,
  otherUserName,
  otherUserImage,
  otherUserOnline,
  matchId = '',
  matchStatus = true,
  blockStatus = { hasBlocked: '0', isBlocked: '0' },
  currentUserId,
  currentUserName,
  currentUserImage,
  currentUserEmail = '',
  jobId,
  jobTitle,
  onFeedback,
  onVideoCall,
  onUnmatch,
  onBlock,
  onReport,
  onConnect,
  connected,
}) => {

  const isChatConnected = useMemo(() => {
    if (connected !== undefined) {
      return connected === true || Number(connected) === 1 || String(connected) === 'true';
    }
    return matchStatus === true || Number(matchStatus) === 1 || String(matchStatus) === 'true';
  }, [connected, matchStatus]);

  const navigate = useNavigate();
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [expandedMessages, setExpandedMessages] = useState<Record<string, boolean>>({});
  const [isMessagesLoading, setIsMessagesLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [previewEditMessage, setPreviewEditMessage] = useState('');
  const [editMessageId, setEditMessageId] = useState('');
  const [previewReply, setPreviewReply] = useState('');
  const [replyChatId, setReplyChatId] = useState('');
  const [replyTo, setReplyTo] = useState('');
  const [isReply, setIsReply] = useState(false);

  // ── Attachment / upload state ──
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [stagedFile, setStagedFile] = useState<File | null>(null);
  const [uploadedUrl, setUploadedUrl] = useState<string | null>(null);
  const [uploadingImagePreview, setUploadingImagePreview] = useState<string | null>(null);
  const uploadPromiseRef = useRef<Promise<{ url: string } | null> | null>(null);

  // ── Audio recording state ──
  const [isRecording, setIsRecording] = useState(false);
  const [recordingSeconds, setRecordingSeconds] = useState(0);
  const recordingSecondsRef = useRef(0); // ref so onstop closure always has current value
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const audioChunksRef = useRef<Blob[]>([]);
  const recordingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const audioStreamRef = useRef<MediaStream | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const animFrameRef = useRef<number | null>(null);
  const waveCanvasRef = useRef<HTMLCanvasElement>(null);

  const chatContainerRef = useRef<HTMLDivElement>(null);
  const emojiRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // ── Typing indicator state ──
  const [otherUserTyping, setOtherUserTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Derive consistent conversation ID (sorted user IDs for regular chat; job-based for job chat)
  const conversationId = useMemo(() => {
    if (jobId) return `job_${jobId}`;
    const ids = [Number(currentUserId), Number(otherUserId)].filter(n => !isNaN(n));
    if (ids.length < 2) return '';
    ids.sort((a, b) => a - b);
    return `${ids[0]}-${ids[1]}`;
  }, [currentUserId, otherUserId, jobId]);

  const { blockedByMe: hasBlocked, blockedByUser: isBlocked, isBlocked: isProfileBlocked } = useMemo(
    () => getCandidateBlockState(blockStatus),
    [blockStatus],
  );

  const handleProfileNavigation = () => {
    if (isProfileBlocked) {
      // toast.error(hasBlocked ? 'Please unblock this user before viewing their profile details.' : 'This profile is unavailable because the user has blocked you.');
      return;
    }
    navigate(`/candidate/${encryptId(otherUserId)}`);
  };
  // NOTE: timestamps are computed fresh inside submitChat / sendMediaMessage at send time

  // When jobId is provided, messages are stored under job_chat/{jobId}/{userId}
  // Each user has their own thread for each job (not shared conversation)
  const chatNs = jobId ? `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/job_chat/${jobId}` : `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/chat`;
  const inboxNs = jobId ? `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/job_chat_inbox` : `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/Inbox`;
  // ── Reset state when switching conversation or job ──
  useEffect(() => {
    setIsReply(false);
    setReplyTo('');
    setPreviewReply('');
    setReplyChatId('');
    setPreviewEditMessage('');
    setEditMessageId('');
    setMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setMessages([]);
    setIsUploading(false);
    setUploadProgress(0);
    // Stop any in-progress recording when switching chats
    if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') mediaRecorderRef.current.stop();
    if (audioStreamRef.current) { audioStreamRef.current.getTracks().forEach(t => t.stop()); audioStreamRef.current = null; }
    analyserRef.current = null;
    recordingSecondsRef.current = 0;
    setIsRecording(false);
    setRecordingSeconds(0);
  }, [otherUserId, jobId]);

  // ── Listen to Firebase messages ──
  useEffect(() => {
    if (!otherUserId || !currentUserId) return;

    setIsMessagesLoading(true);

    // For job chats: each user has their own thread at job_chat/{jobId}/{userId}
    // For regular chats: shared threads at chat/{user1}-{user2}
    const isJobChat = !!jobId;

    let path1: string;
    let path2: string;

    if (isJobChat) {
      // Job chat: only store under candidate's (other user's) thread — no company node needed
      path1 = `${chatNs}/${otherUserId}`;
      path2 = path1; // Same path, no need for bidirectional
    } else {
      // Regular chat: listen to both directions
      path1 = `${chatNs}/${currentUserId}-${otherUserId}`;
      path2 = `${chatNs}/${otherUserId}-${currentUserId}`;
    }

    let msgs1: any[] = [];
    let msgs2: any[] = [];

    const merge = async () => {
      const all = isJobChat ? msgs1 : [...msgs1, ...msgs2];
      const unique = all.reduce((acc: any[], m) => {
        if (!acc.find((x) => x.chat_id === m.chat_id)) acc.push(m);
        return acc;
      }, []);
      unique.sort((a, b) => {
        const dA = parseDayjsDate(a.timestamp || a.time);
        const dB = parseDayjsDate(b.timestamp || b.time);
        const tA = dA ? dA.valueOf() : 0;
        const tB = dB ? dB.valueOf() : 0;
        return tA - tB;
      });

      console.log(`[ChatBoard] Total messages in chat: ${unique.length}`);
      console.log('[ChatBoard] Raw message timestamps & dates list:', unique.map((m, idx) => ({
        index: idx,
        chat_id: m.chat_id,
        sender: m.sender_name,
        raw_time: m.time,
        raw_timestamp: m.timestamp,
        parsed_iso: parseDayjsDate(m.timestamp || m.time)?.format('YYYY-MM-DD HH:mm:ss') || 'INVALID_DATE',
        text_preview: (m.decryptMsg || m.text || '').substring(0, 30)
      })));

      setMessages(unique);
      setIsMessagesLoading(false);
      setTimeout(() => {
        if (chatContainerRef.current) {
          chatContainerRef.current.scrollTop = chatContainerRef.current.scrollHeight;
        }
      }, 80);
    };

    const decode = async (entries: Record<string, any>) =>
      Promise.all(
        Object.entries(entries).map(async ([, val]) => {
          const decryptMsg = val?.text ? await decrypt(val.text).catch(() => '') : '';
          const replyMsgDecrypt = val?.parentMessage?.text ? await decrypt(val.parentMessage.text).catch(() => '') : '';

          let decryptPicUrl = val?.pic_url || val?.url || val?.file_url || val?.audio_url || val?.media_url || val?.audio || val?.voice || val?.file || '';
          if (!decryptPicUrl && val?.type === 'audio' && decryptMsg && (decryptMsg.startsWith('http') || decryptMsg.startsWith('blob:') || decryptMsg.startsWith('data:'))) {
            decryptPicUrl = decryptMsg;
          }

          if (
            decryptPicUrl &&
            typeof decryptPicUrl === 'string' &&
            !decryptPicUrl.startsWith('http') &&
            !decryptPicUrl.startsWith('blob:') &&
            !decryptPicUrl.startsWith('data:')
          ) {
            const dec = await decrypt(decryptPicUrl).catch(() => '');
            if (dec && (dec.startsWith('http') || dec.startsWith('blob:') || dec.startsWith('data:'))) {
              decryptPicUrl = dec;
            }
          }

          return { ...val, decryptMsg, replyMsgDecrypt, decryptPicUrl };
        })
      );

    const u1 = onValue(ref(rtdb, path1), async (snap: any) => {
      try {
        msgs1 = snap.val() ? await decode(snap.val()) : [];
        merge();
      } catch (err) {
        console.error('[ChatBoard] path1 process error:', err);
        msgs1 = [];
        merge();
      }
    });

    // For job chats, we don't need to listen to path2 since it's the same
    const u2 = isJobChat ? (() => { }) : onValue(ref(rtdb, path2), async (snap: any) => {
      try {
        msgs2 = snap.val() ? await decode(snap.val()) : [];
        merge();
      } catch (err) {
        console.error('[ChatBoard] path2 process error:', err);
        msgs2 = [];
        merge();
      }
    });

    return () => { u1(); u2(); };
  }, [otherUserId, currentUserId, chatNs, jobId]);

  // ── Close emoji picker on outside click ──
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (emojiRef.current && !emojiRef.current.contains(e.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Typing indicator: listen to other user ──
  useEffect(() => {
    if (!conversationId || !otherUserId || hasBlocked || isBlocked) {
      setOtherUserTyping(false);
      return;
    }

    const typingRef = ref(rtdb, `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/typing/${conversationId}/${otherUserId}`);
    const unsub = onValue(typingRef, (snap) => {
      const data = snap.val();
      setOtherUserTyping(data?.typing === true);
    });

    return () => { unsub(); setOtherUserTyping(false); };
  }, [conversationId, otherUserId, hasBlocked, isBlocked]);

  // ── Typing indicator: set when current user types ──
  const setTyping = useCallback((isTyping: boolean) => {
    if (!conversationId || !currentUserId || hasBlocked || isBlocked) return;
    const typingRef = ref(rtdb, `${process.env.REACT_APP_FIREBASE_ENV === "staging" ? "staging" : "prod"}/typing/${conversationId}/${currentUserId}`);
    if (isTyping) {
      set(typingRef, { typing: true, name: currentUserName, timestamp: Date.now() });
      // Auto-clear after 3 seconds in case component unmounts or input hangs
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      typingTimeoutRef.current = setTimeout(() => {
        remove(typingRef).catch(() => { });
      }, 3000);
    } else {
      if (typingTimeoutRef.current) clearTimeout(typingTimeoutRef.current);
      remove(typingRef).catch(() => { });
    }
  }, [conversationId, currentUserId, currentUserName, hasBlocked, isBlocked]);

  // ── Handle input change for typing indicator & auto-resize ──
  const handleInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    const val = e.target.value;
    setMessage(val);
    if (e.target instanceof HTMLTextAreaElement) {
      e.target.style.height = 'auto';
      e.target.style.height = `${Math.min(e.target.scrollHeight, 120)}px`;
    }
    if (val.trim()) {
      setTyping(true);
    } else {
      setTyping(false);
    }
  }, [setTyping]);

  // ── Cleanup typing on unmount ──
  useEffect(() => {
    return () => {
      setTyping(false);
    };
  }, [setTyping]);

  // ── Stop recording on unmount ──
  useEffect(() => {
    return () => {
      if (recordingTimerRef.current) clearInterval(recordingTimerRef.current);
      if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (audioStreamRef.current) audioStreamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  // ── Send message ──
  const submitChat = async () => {
    if (!currentUserId) return;
    if (!isChatConnected) {
      toast.error('Cannot send messages to unmatched users. Send a connect request first.');
      return;
    }

    // If there is a staged image, send pre-uploaded image along with optional text caption
    if (stagedFile || uploadingImagePreview) {
      const captionText = message;
      setMessage('');
      setTyping(false);

      let finalUrl = uploadedUrl;
      if (!finalUrl && uploadPromiseRef.current) {
        setIsUploading(true);
        const res = await uploadPromiseRef.current;
        finalUrl = res?.url || null;
      }

      if (finalUrl) {
        await sendMediaMessageWithUrl(finalUrl, 'image', captionText);
      } else {
        toast.error('Image upload failed. Please re-select the image and try again.');
      }
      return;
    }

    if (!message.trim()) return;
    const toSend = message;
    setMessage('');
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
    }
    setTyping(false); // clear typing indicator immediately on send
    try {
      const enc = await encrypt(toSend);
      const ts = dayjs().format('DD-MM-YYYY HH:mm:ssZZ');
      const tsMs = dayjs().valueOf();

      // Helper to sanitize objects for Firebase
      const sanitize = (obj: any) => JSON.parse(JSON.stringify(obj));

      const isJobChat = !!jobId;

      // Update inbox
      let recipientUnreadCount = 1;
      try {
        const recipientInboxPath = isJobChat 
          ? `${inboxNs}/${otherUserId}/${jobId}` 
          : `${inboxNs}/${otherUserId}/${currentUserId}`;
        const recipientSnap = await get(ref(rtdb, recipientInboxPath));
        if (recipientSnap.exists()) {
          const val = recipientSnap.val();
          const prevCount = Number(val?.unread_count ?? (val?.status === '1' ? 1 : 0)) || 0;
          recipientUnreadCount = prevCount + 1;
        }
      } catch (err) {
        console.warn('Failed to fetch recipient unread count in ChatBoard:', err);
      }

      const inboxMe = sanitize({
        block: '0',
        date: ts,
        matchId,
        matchStatus: true,
        msg: enc,
        name: otherUserName,
        pic: otherUserImage || '',
        rid: otherUserId,
        status: '0',
        unread_count: 0,
        timestamp: `-${tsMs}`,
        hasInitiatedChat: true,
        ...(jobId ? { jobId, jobTitle } : {})
      });

      const inboxOther = sanitize({
        block: '0',
        date: ts,
        matchId,
        matchStatus: true,
        msg: enc,
        name: currentUserName,
        pic: currentUserImage || '',
        rid: currentUserId,
        status: '1',
        unread_count: recipientUnreadCount,
        timestamp: `-${tsMs}`,
        hasInitiatedChat: true,
        ...(jobId ? { jobId, jobTitle } : {})
      });

      if (isJobChat) {
        // Job chat inbox: job_chat_inbox/{userId}/{jobId}
        await update(ref(rtdb, `${inboxNs}/${currentUserId}/${jobId}`), inboxMe);
        if (!isBlocked) {
          await update(ref(rtdb, `${inboxNs}/${otherUserId}/${jobId}`), inboxOther);
        }
        // Messages stored under candidate's node only: job_chat/{jobId}/{otherUserId}
      } else {
        // Regular chat inbox
        await update(ref(rtdb, `${inboxNs}/${currentUserId}/${otherUserId}`), inboxMe);
        if (!isBlocked) {
          await update(ref(rtdb, `${inboxNs}/${otherUserId}/${currentUserId}`), inboxOther);
        }
      }

      if (!editMessageId && !isReply) {
        // Send new message
        const chatPath = isJobChat
          ? `${chatNs}/${otherUserId}`
          : `${chatNs}/${currentUserId}-${otherUserId}`;

        const newRef = push(ref(rtdb, chatPath));
        const pushId = newRef.key;
        const chatData = sanitize({
          chat_id: pushId,
          pic_url: otherUserImage || '',
          receiver_id: otherUserId,
          isReply: false,
          sender_id: currentUserId,
          sender_name: currentUserName,
          status: '0',
          text: enc,
          textAnalysis: '',
          time: ts,
          timestamp: ts,
          type: 'text',
          ...(jobId ? { jobId, jobTitle } : {})
        });

        if (isJobChat) {
          await set(ref(rtdb, `${chatNs}/${otherUserId}/${pushId}`), chatData);
        } else {
          await set(ref(rtdb, `${chatNs}/${currentUserId}-${otherUserId}/${pushId}`), chatData);
          if (!isBlocked) {
            await set(ref(rtdb, `${chatNs}/${otherUserId}-${currentUserId}/${pushId}`), chatData);
          }
        }
        // Fire-and-forget push notification to receiver
        if (!isBlocked) {
          apiService.sendChatMessageNotification({
            receiver_id: Number(otherUserId),
            message: toSend,
            job_id: jobId ? Number(jobId) : null,
            job_title: jobTitle || null,
          }).catch((e) => console.warn('[ChatBoard] push notification failed:', e));
        }
      } else if (editMessageId) {
        // Edit message
        if (isJobChat) {
          await update(ref(rtdb, `${chatNs}/${otherUserId}/${editMessageId}`), { idEdited: true, text: enc });
        } else {
          await update(ref(rtdb, `${chatNs}/${currentUserId}-${otherUserId}/${editMessageId}`), { idEdited: true, text: enc });
          if (!isBlocked) {
            await update(ref(rtdb, `${chatNs}/${otherUserId}-${currentUserId}/${editMessageId}`), { idEdited: true, text: enc });
          }
        }
        setPreviewEditMessage('');
        setEditMessageId('');
      } else if (isReply) {
        // Reply to message
        const chatPath = isJobChat
          ? `${chatNs}/${otherUserId}`
          : `${chatNs}/${currentUserId}-${otherUserId}`;

        const snap = await get(ref(rtdb, `${chatPath}/${replyChatId}`));
        const newRef = push(ref(rtdb, chatPath));
        const pushId = newRef.key;

        let parentMsg = null;
        if (snap.exists()) {
          const val = snap.val();
          parentMsg = {
            chat_id: val.chat_id,
            text: val.text,
            sender_name: val.sender_name,
            type: val.type || 'text'
          };
        }

        const chatData = sanitize({
          chat_id: pushId,
          pic_url: otherUserImage || '',
          receiver_id: otherUserId,
          isReply: true,
          parentMessage: parentMsg,
          sender_id: currentUserId,
          sender_name: currentUserName,
          status: '0',
          text: enc,
          textAnalysis: '',
          time: ts,
          timestamp: ts,
          type: 'text',
          ...(jobId ? { jobId, jobTitle } : {})
        });

        if (isJobChat) {
          await set(ref(rtdb, `${chatNs}/${otherUserId}/${pushId}`), chatData);
        } else {
          await set(ref(rtdb, `${chatNs}/${currentUserId}-${otherUserId}/${pushId}`), chatData);
          if (!isBlocked) {
            await set(ref(rtdb, `${chatNs}/${otherUserId}-${currentUserId}/${pushId}`), chatData);
          }
        }
        // Fire-and-forget push notification to receiver
        if (!isBlocked) {
          apiService.sendChatMessageNotification({
            receiver_id: Number(otherUserId),
            message: toSend,
            job_id: jobId ? Number(jobId) : null,
            job_title: jobTitle || null,
          }).catch((e) => console.warn('[ChatBoard] push notification failed:', e));
        }
        setIsReply(false);
        setReplyTo('');
        setPreviewReply('');
        setReplyChatId('');
      }
    } catch (err) {
      console.error('[ChatBoard] send error', err);
      toast.error('Failed to send message');
      setMessage(toSend);
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement | HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); if (message.trim() || stagedFile) submitChat(); }
  };

  const handleEmojiClick = (data: EmojiClickData) => {
    setMessage((p) => {
      const next = p + data.emoji;
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = 'auto';
          textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
      }, 0);
      return next;
    });
  };

  const deleteMsg = async (chatId: string) => {
    try {
      const isJobChat = !!jobId;
      // Mark as deleted (same as mobile app) — update type to 'delete' and clear text
      // so the node stays in Firebase and both sides see "This message is deleted"
      const deletedPayload = { type: 'delete', text: '' };
      if (isJobChat) {
        await update(ref(rtdb, `${chatNs}/${otherUserId}/${chatId}`), deletedPayload);
      } else {
        await update(ref(rtdb, `${chatNs}/${currentUserId}-${otherUserId}/${chatId}`), deletedPayload);
        await update(ref(rtdb, `${chatNs}/${otherUserId}-${currentUserId}/${chatId}`), deletedPayload);
      }
      toast.success('Message deleted');
    } catch { toast.error('Failed to delete'); }
  };

  const copyMsg = (text: string) => navigator.clipboard.writeText(text).then(() => toast.success('Copied!')).catch(() => { });

  const editMsg = (text: string, id: string) => {
    setMessage(text);
    setPreviewEditMessage(text);
    setEditMessageId(id);
    setTimeout(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        textareaRef.current.focus();
      }
    }, 0);
  };

  const replyMsg = (text: string, senderName: string, id: string) => { setIsReply(true); setReplyTo(senderName); setPreviewReply(text); setReplyChatId(id); };

  // ── Send a media message node to Firebase via pre-uploaded URL ──────────────────────────
  const sendMediaMessageWithUrl = async (picUrl: string, msgType: 'image' | 'audio' | 'file', textCaption?: string, durationSecs?: number) => {
    if (!currentUserId) return;
    try {
      const ts = dayjs().format('DD-MM-YYYY HH:mm:ssZZ');
      const tsMs = dayjs().valueOf();
      const isJobChat = !!jobId;
      const sanitize = (obj: any) => JSON.parse(JSON.stringify(obj));

      const durationStr = durationSecs != null
        ? `${Math.floor(durationSecs / 60).toString().padStart(2, '0')}:${(durationSecs % 60).toString().padStart(2, '0')}`
        : undefined;

      const trimmedCaption = textCaption?.trim() || '';
      const encText = trimmedCaption ? await encrypt(trimmedCaption) : '';
      const defaultLabel = msgType === 'image' ? 'Send an Image' : msgType === 'audio' ? 'Send an Audio' : 'Send a File';

      // Update inbox
      const inboxPayload = sanitize({
        block: '0', date: ts, matchId, matchStatus: true,
        msg: encText || defaultLabel,
        name: otherUserName, pic: otherUserImage || '', rid: otherUserId,
        status: '0', timestamp: `-${tsMs}`, hasInitiatedChat: true,
        ...(jobId ? { jobId, jobTitle } : {}),
      });
      const inboxOtherPayload = sanitize({
        ...inboxPayload,
        name: currentUserName, pic: currentUserImage || '', rid: currentUserId, status: '1',
      });

      if (isJobChat) {
        await update(ref(rtdb, `${inboxNs}/${currentUserId}/${jobId}`), inboxPayload);
        if (!isBlocked) {
          await update(ref(rtdb, `${inboxNs}/${otherUserId}/${jobId}`), inboxOtherPayload);
        }
      } else {
        await update(ref(rtdb, `${inboxNs}/${currentUserId}/${otherUserId}`), inboxPayload);
        if (!isBlocked) {
          await update(ref(rtdb, `${inboxNs}/${otherUserId}/${currentUserId}`), inboxOtherPayload);
        }
      }

      const chatPath = isJobChat ? `${chatNs}/${otherUserId}` : `${chatNs}/${currentUserId}-${otherUserId}`;
      const newRef = push(ref(rtdb, chatPath));
      const pushId = newRef.key;

      // Firebase node — matches app structure exactly
      const chatData = sanitize({
        chat_id: pushId,
        pic_url: picUrl,
        receiver_id: otherUserId,
        isReply: false,
        sender_id: currentUserId,
        sender_name: currentUserName,
        status: '0',
        text: encText || defaultLabel,
        textAnalysis: '',
        time: ts,
        timestamp: ts,
        type: msgType,
        ...(durationStr ? { duration: durationStr } : {}),
        ...(jobId ? { jobId, jobTitle } : {}),
      });

      if (isJobChat) {
        await set(ref(rtdb, `${chatNs}/${otherUserId}/${pushId}`), chatData);
      } else {
        await set(ref(rtdb, `${chatNs}/${currentUserId}-${otherUserId}/${pushId}`), chatData);
        if (!isBlocked) {
          await set(ref(rtdb, `${chatNs}/${otherUserId}-${currentUserId}/${pushId}`), chatData);
        }
      }

      // Push notification
      if (!isBlocked) {
        const notifMsg = trimmedCaption
          ? `📷 ${trimmedCaption}`
          : (msgType === 'image' ? '📷 Sent an image' : msgType === 'audio' ? '🎤 Sent a voice message' : `📎 Sent a file`);
        apiService.sendChatMessageNotification({
          receiver_id: Number(otherUserId), message: notifMsg,
          job_id: jobId ? Number(jobId) : null, job_title: jobTitle || null,
        }).catch(() => { });
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to send message');
    } finally {
      setIsUploading(false);
      setUploadProgress(0);
      setStagedFile(null);
      setUploadedUrl(null);
      uploadPromiseRef.current = null;
      setUploadingImagePreview((prevUrl) => {
        if (prevUrl) URL.revokeObjectURL(prevUrl);
        return null;
      });
    }
  };

  // ── Send a media message (image / audio / file) ──────────────────────────
  const sendMediaMessage = async (file: File, msgType: 'image' | 'audio' | 'file', textCaption?: string, durationSecs?: number) => {
    if (!currentUserId) return;
    setIsUploading(true);
    setUploadProgress(0);
    try {
      const folder = `Media/User/${currentUserId}/Chat/${msgType === 'image' ? 'Images' : msgType === 'audio' ? 'Audio' : 'Files'}`;
      const uploaded = await apiService.uploadChatFile(file, folder, setUploadProgress);
      await sendMediaMessageWithUrl(uploaded.url, msgType, textCaption, durationSecs);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Failed to send file');
      setIsUploading(false);
      setUploadProgress(0);
    }
  };

  // ── File attachment handler ───────────────────────────────────────────────
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so same file can be re-selected
    e.target.value = '';
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Only JPEG, PNG, GIF, and WebP are allowed');
      return;
    }
    if (uploadingImagePreview) URL.revokeObjectURL(uploadingImagePreview);
    const previewUrl = URL.createObjectURL(file);

    setStagedFile(file);
    setUploadedUrl(null);
    setUploadingImagePreview(previewUrl);
    setIsUploading(true);
    setUploadProgress(0);

    // Immediately start background file upload upon selection
    const folder = `Media/User/${currentUserId}/Chat/Images`;
    const uploadPromise = apiService.uploadChatFile(file, folder, setUploadProgress)
      .then((uploaded) => {
        setUploadedUrl(uploaded.url);
        setIsUploading(false);
        setUploadProgress(100);
        return uploaded;
      })
      .catch((err: any) => {
        console.error('[ChatBoard] Background image upload error:', err);
        toast.error(err?.response?.data?.message || err?.message || 'Failed to upload image');
        setIsUploading(false);
        setUploadProgress(0);
        setStagedFile(null);
        setUploadedUrl(null);
        setUploadingImagePreview((prev) => {
          if (prev) URL.revokeObjectURL(prev);
          return null;
        });
        return null;
      });

    uploadPromiseRef.current = uploadPromise;
  };

  // ── Audio recording ───────────────────────────────────────────────────────
  const stopRecording = () => {
    if (recordingTimerRef.current) { clearInterval(recordingTimerRef.current); recordingTimerRef.current = null; }
    if (animFrameRef.current) { cancelAnimationFrame(animFrameRef.current); animFrameRef.current = null; }
    // Stop the recorder — onstop will fire and read recordingSecondsRef BEFORE we reset it
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop(); // onstop fires synchronously after this in most browsers
    }
    if (audioStreamRef.current) { audioStreamRef.current.getTracks().forEach(t => t.stop()); audioStreamRef.current = null; }
    analyserRef.current = null;
    // Reset AFTER stop() so onstop closure still reads the correct duration
    setTimeout(() => {
      recordingSecondsRef.current = 0;
      setIsRecording(false);
      setRecordingSeconds(0);
    }, 0);
    // Clear canvas immediately for UX
    const canvas = waveCanvasRef.current;
    if (canvas) { const ctx = canvas.getContext('2d'); if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height); }
  };

  const drawWaveform = () => {
    const analyser = analyserRef.current;
    const canvas = waveCanvasRef.current;
    if (!analyser || !canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const bufferLength = analyser.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);

    const draw = () => {
      animFrameRef.current = requestAnimationFrame(draw);
      analyser.getByteTimeDomainData(dataArray);

      ctx.clearRect(0, 0, canvas.width, canvas.height);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#457EFF';
      ctx.beginPath();

      const sliceWidth = canvas.width / bufferLength;
      let x = 0;
      for (let i = 0; i < bufferLength; i++) {
        const v = dataArray[i] / 128.0;
        const y = (v * canvas.height) / 2;
        if (i === 0) ctx.moveTo(x, y);
        else ctx.lineTo(x, y);
        x += sliceWidth;
      }
      ctx.lineTo(canvas.width, canvas.height / 2);
      ctx.stroke();
    };
    draw();
  };

  const startRecording = async () => {
    if (isRecording) { stopRecording(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStreamRef.current = stream;
      audioChunksRef.current = [];

      // Set up analyser for waveform visualisation
      const audioCtx = new AudioContext();
      const source = audioCtx.createMediaStreamSource(stream);
      const analyser = audioCtx.createAnalyser();
      analyser.fftSize = 256;
      source.connect(analyser);
      analyserRef.current = analyser;

      const mimeType = MediaRecorder.isTypeSupported('audio/webm;codecs=opus')
        ? 'audio/webm;codecs=opus'
        : MediaRecorder.isTypeSupported('audio/webm')
          ? 'audio/webm'
          : 'audio/mp4';

      const recorder = new MediaRecorder(stream, { mimeType });
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) audioChunksRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const fileMime = mimeType.split(';')[0];
        const ext = fileMime === 'audio/mp4' ? 'm4a' : 'webm';
        const blob = new Blob(audioChunksRef.current, { type: fileMime });
        const file = new File([blob], `voice-${Date.now()}.${ext}`, { type: fileMime });
        const duration = recordingSecondsRef.current;
        await sendMediaMessage(file, 'audio', undefined, duration);
      };

      recorder.start(250); // collect data every 250ms so chunks are never empty
      setIsRecording(true);
      setRecordingSeconds(0);
      recordingTimerRef.current = setInterval(() => {
        recordingSecondsRef.current += 1;
        setRecordingSeconds(recordingSecondsRef.current);
      }, 1000);
      // Start waveform after state update
      setTimeout(drawWaveform, 50);
    } catch {
      toast.error('Microphone access denied');
    }
  };

  // Derive audio MIME type from URL extension so the browser can decode it
  const getAudioMime = (url?: string) => {
    if (!url) return undefined;
    const ext = url.split('?')[0].split('.').pop()?.toLowerCase();
    const map: Record<string, string> = {
      webm: 'audio/webm', mp4: 'audio/mp4', m4a: 'audio/mp4',
      mp3: 'audio/mpeg', ogg: 'audio/ogg', wav: 'audio/wav', aac: 'audio/aac',
    };
    return map[ext || ''] || undefined;
  };

  // ── Format message time for display (e.g. "02:30 PM") ──
  const formatMessageTime = (timeStr?: string) => {
    if (!timeStr) return '';
    const d = parseDayjsDate(timeStr);
    if (d && d.isValid()) {
      return d.format('hh:mm A');
    }
    return timeStr;
  };

  const parseDayjsDate = (val?: any): dayjs.Dayjs | null => {
    if (val === null || val === undefined || val === '') return null;
    if (typeof val === 'number') {
      const d = dayjs(Math.abs(val));
      return d.isValid() ? d : null;
    }
    const str = String(val).trim();
    if (/^-?\d+$/.test(str)) {
      const d = dayjs(Math.abs(Number(str)));
      return d.isValid() ? d : null;
    }
    const ddMmMatch = str.match(/^(\d{1,2})[-/](\d{1,2})[-/](\d{4})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (ddMmMatch) {
      const [, p1, p2, year, hr = '00', min = '00', sec = '00'] = ddMmMatch;
      const day = p1.padStart(2, '0');
      const month = p2.padStart(2, '0');
      const d = dayjs(`${year}-${month}-${day}T${hr.padStart(2, '0')}:${min}:${sec}`);
      if (d.isValid()) return d;
    }
    const isoMatch = str.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?/);
    if (isoMatch) {
      const [, year, month, day, hr = '00', min = '00', sec = '00'] = isoMatch;
      const d = dayjs(`${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}T${hr.padStart(2, '0')}:${min}:${sec}`);
      if (d.isValid()) return d;
    }
    const timeOnlyMatch = str.match(/^(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(AM|PM|am|pm)?$/i);
    if (timeOnlyMatch) {
      let [, hrStr, minStr, secStr = '00', ampm] = timeOnlyMatch;
      let hr = Number(hrStr);
      const min = Number(minStr);
      if (ampm) {
        const isPm = ampm.toUpperCase() === 'PM';
        if (isPm && hr < 12) hr += 12;
        if (!isPm && hr === 12) hr = 0;
      }
      const d = dayjs().hour(hr).minute(min).second(Number(secStr));
      if (d.isValid()) return d;
    }
    const d = dayjs(str);
    return d.isValid() ? d : null;
  };

  // ── Check if two timestamps fall on different calendar days ──
  const isDifferentDay = (time1?: string, time2?: string): boolean => {
    const d1 = parseDayjsDate(time1);
    const d2 = parseDayjsDate(time2);
    if (!d1 || !d2) return false;
    return !d1.isSame(d2, 'day');
  };

  // ── Get date label for separator banner (Today / Yesterday / "August 10" / "August 10, 2025") ──
  const getDateSeparator = (timeStr?: string): React.ReactNode | null => {
    const d = parseDayjsDate(timeStr);
    if (!d) {
      console.warn('[ChatBoard] getDateSeparator failed to parse timestamp:', timeStr);
      return null;
    }
    const today = dayjs();
    const yesterday = dayjs().subtract(1, 'day');
    let label: string;
    if (d.isSame(today, 'day')) {
      label = 'Today';
    } else if (d.isSame(yesterday, 'day')) {
      label = 'Yesterday';
    } else if (d.isSame(today, 'year')) {
      label = d.format('MMMM D');
    } else {
      label = d.format('MMMM D, YYYY');
    }
    console.log(`[ChatBoard DateBanner] Rendered banner "${label}" for raw time: "${timeStr}" (Parsed: ${d.format('YYYY-MM-DD HH:mm:ss')})`);
    return <div className="chat-date-separator"><span>{label}</span></div>;
  };

  // ── SVG helpers (same icons as Chats.tsx) ──
  const CopyIcon = () => (
    <svg width="1em" height="1em" viewBox="0 0 18 18" fill="none"><path d="M8.1949 15.038H12.7838C12.5457 15.6331 12.1345 16.143 11.6035 16.5018C11.0724 16.8606 10.4459 17.0518 9.80503 17.0507H4.16951C3.74658 17.0508 3.32777 16.9676 2.93702 16.8058C2.54626 16.644 2.19121 16.4068 1.89215 16.1078C1.5931 15.8087 1.3559 15.4537 1.19411 15.0629C1.03231 14.6722 0.9491 14.2533 0.949219 13.8304V7.3898C0.950172 6.6056 1.23674 5.8486 1.75535 5.26036C2.27395 4.67212 2.98906 4.29293 3.76696 4.1937V10.6101C3.76696 13.0495 5.75548 15.038 8.1949 15.038ZM15.038 4.37077H16.7448C16.6953 4.29821 16.6386 4.23079 16.5757 4.16951L13.8304 1.42408C13.7711 1.3614 13.7034 1.30729 13.6292 1.26331V2.9619C13.6309 3.335 13.7799 3.69232 14.0438 3.95616C14.3076 4.21999 14.6649 4.36899 15.038 4.37077ZM15.038 5.57838C14.3445 5.57704 13.6798 5.30095 13.1894 4.81055C12.699 4.32016 12.4229 3.65542 12.4215 2.9619V0.949219H8.1949C7.77197 0.9491 7.35316 1.03231 6.9624 1.19411C6.57164 1.3559 6.21659 1.5931 5.91753 1.89215C5.61848 2.19121 5.38127 2.54626 5.21947 2.93701C5.05768 3.32777 4.97446 3.74658 4.97457 4.16951V10.6101C4.97446 11.0331 5.05769 11.4519 5.21948 11.8426C5.38128 12.2334 5.61849 12.5884 5.91754 12.8875C6.2166 13.1865 6.57165 13.4237 6.96241 13.5855C7.35317 13.7473 7.77197 13.8305 8.1949 13.8304H13.8304C14.2533 13.8305 14.6722 13.7473 15.0629 13.5855C15.4537 13.4237 15.8087 13.1865 16.1078 12.8875C16.4068 12.5884 16.644 12.2334 16.8058 11.8426C16.9676 11.4519 17.0508 11.0331 17.0507 10.6101V5.57838H15.038Z" fill="currentcolor" /></svg>
  );
  const DeleteIcon = () => (
    <svg width="1em" height="1em" viewBox="0 0 18 18" fill="none"><path d="M16.8122 3.1779H12.9494V2.4147C12.9494 1.3329 12.089 0.470703 11.0054 0.470703H7.0202C5.9384 0.470703 5.0762 1.3311 5.0762 2.4147V3.1779H1.1882C0.770595 3.1779 0.450195 3.4983 0.450195 3.9159C0.450195 4.3335 0.770595 4.6539 1.1882 4.6539H2.3942V15.6087C2.4194 16.6905 3.2798 17.5527 4.3634 17.5275H13.6388C14.7206 17.5527 15.5828 16.6905 15.608 15.6087V4.6539H16.814C17.2316 4.6539 17.552 4.3335 17.552 3.9159C17.5502 3.4983 17.2298 3.1779 16.8122 3.1779ZM6.5522 2.4165C6.527 2.1699 6.725 1.9737 6.9698 1.9485H11.0054C11.252 1.9233 11.4482 2.1213 11.4734 2.3661V3.1779H6.5522V2.4165ZM7.53679 12.9771C7.53679 13.3947 7.21639 13.7151 6.7988 13.7151C6.3812 13.7151 6.06079 13.3947 6.06079 12.9771V7.5087C6.06079 7.0911 6.3812 6.7707 6.7988 6.7707C7.21639 6.7707 7.53679 7.0911 7.53679 7.5087V12.9771ZM11.9648 12.9771C11.9648 13.3947 11.6444 13.7151 11.2268 13.7151C10.8092 13.7151 10.4888 13.3947 10.4888 12.9771V7.5087C10.4888 7.0911 10.8092 6.7707 11.2268 6.7707C11.6444 6.7707 11.9648 7.0911 11.9648 7.5087V12.9771Z" fill="currentcolor" /></svg>
  );
  const ReplyIcon = () => (
    <svg width="18" height="18" viewBox="0 0 18 18" fill="none"><path d="M10.1195 5.95747H3.24749L6.26717 2.93779C6.58913 2.61583 6.58913 2.08295 6.26717 1.761C5.94522 1.43904 5.41234 1.43904 5.09038 1.761L0.649667 6.20171C0.327715 6.52367 0.327715 7.05655 0.649667 7.3785L5.09038 11.8192C5.25691 11.9857 5.46785 12.0635 5.67878 12.0635C5.88971 12.0635 6.10065 11.9857 6.26717 11.8192C6.58913 11.4973 6.58913 10.9644 6.26717 10.6424L3.24749 7.62274H10.1195C13.339 7.62274 15.9479 10.2428 15.9479 13.4512V15.6715C15.9479 16.1267 16.3254 16.5042 16.7806 16.5042C17.2357 16.5042 17.6132 16.1267 17.6132 15.6715V13.4512C17.6132 9.32132 14.2494 5.95747 10.1195 5.95747Z" fill="currentcolor" /></svg>
  );
  const EditIcon = () => (
    <svg width="1em" height="1em" viewBox="0 0 18 18" fill="none"><g clipPath="url(#cb_edit)"><path d="M9.48 4.06425L1.7625 11.7818C1.47812 12.0667 1.2984 12.4393 1.2525 12.8393L0.945002 15.5392C0.924958 15.7291 0.945189 15.9211 1.00438 16.1026C1.06356 16.2841 1.16037 16.4511 1.28848 16.5927C1.4166 16.7342 1.57313 16.8472 1.74786 16.9241C1.92259 17.0011 2.11158 17.0403 2.3025 17.0392H2.46L5.16 16.7317C5.55833 16.6857 5.93019 16.509 6.2175 16.2292L13.935 8.50425L9.48 4.06425ZM16.545 3.41175L14.5875 1.45425C14.4238 1.28984 14.2291 1.15939 14.0149 1.07038C13.8006 0.981368 13.5708 0.935547 13.3388 0.935547C13.1067 0.935547 12.8769 0.981368 12.6627 1.07038C12.4484 1.15939 12.2538 1.28984 12.09 1.45425L10.275 3.26925L14.73 7.72425L16.545 5.90925C16.7094 5.7455 16.8399 5.55089 16.9289 5.3366C17.0179 5.1223 17.0637 4.89254 17.0637 4.6605C17.0637 4.42846 17.0179 4.1987 16.9289 3.9844C16.8399 3.77011 16.7094 3.5755 16.545 3.41175Z" fill="currentcolor" /></g><defs><clipPath id="cb_edit"><rect width="18" height="18" fill="white" /></clipPath></defs></svg>
  );

  // ── Header icons (feedback, video, more) ──
  const FeedbackSvg = () => (
    <svg width="1em" className='btn-fb-hover' height="1em" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: 'pointer' }} onClick={onFeedback}>
      <path d="M0 6C0 2.68629 2.68629 0 6 0H28C31.3137 0 34 2.68629 34 6V28C34 31.3137 31.3137 34 28 34H6C2.68629 34 0 31.3137 0 28V6Z" fill="#457EFF" fillOpacity="0.06" />
      <path d="M6 0.5H28C31.0376 0.5 33.5 2.96243 33.5 6V28C33.5 31.0376 31.0376 33.5 28 33.5H6C2.96243 33.5 0.5 31.0376 0.5 28V6C0.5 2.96243 2.96243 0.5 6 0.5Z" stroke="#457EFF" strokeOpacity="0.6" />
      <path d="M22.5182 20.2742L21.9635 23.4532C21.8242 24.2492 21.1332 24.8298 20.3254 24.8298H17.1263C16.9049 24.8296 16.6857 24.7861 16.481 24.7017C16.2782 24.6173 16.0917 24.4942 15.9338 24.3361C15.9242 24.3264 15.9142 24.3167 15.9042 24.307V19.0264L15.9254 19.0189C16.148 18.939 16.3495 18.8093 16.5144 18.6396C16.6792 18.47 16.8031 18.2649 16.8767 18.0401L17.1563 17.1451L17.5332 15.9392C17.6442 15.6001 18.0085 15.4154 18.3479 15.5264C18.9692 15.7289 19.3642 16.3064 19.3642 16.9261C19.3642 17.0779 19.3404 17.2323 19.2904 17.3842L18.8704 18.7623H21.2476C22.0488 18.7623 22.6563 19.4851 22.5182 20.2742ZM14.5007 24.9986H12.3308C12.0734 24.9986 11.8647 24.7899 11.8647 24.5325V18.913C11.8647 18.6556 12.0734 18.4469 12.3308 18.4469H14.5007C14.7581 18.4469 14.9667 18.6556 14.9667 18.913V24.5325C14.9667 24.7899 14.7581 24.9986 14.5007 24.9986Z" fill="#457EFF" />
      <path d="M15.8797 13.8999L17 13.4354L18.1203 13.8999C18.2752 13.9416 18.5104 13.8932 18.6918 13.7358C18.7732 13.5901 18.7669 13.4298 18.6716 12.2208L19.4594 11.2985C19.5627 11.0856 19.5488 10.8493 19.2122 10.5383L18.0331 10.2552L17.3997 9.22091C17.2311 8.92635 16.7689 8.92638 16.6003 9.22091L15.9669 10.2552L14.7878 10.5383C14.4513 10.7043 14.4217 11.0069 14.5406 11.2985L15.3285 12.2208L15.2331 13.4298C15.2268 13.5098 15.3599 13.7989 15.6452 13.9323C15.7249 13.9417 15.8056 13.9305 15.8797 13.8999Z" fill="#FFC107" />
    </svg>
  );

  const VideoSvg = () => (
    <div style={{ position: 'relative', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }} title="Video Call — Coming Soon">
      <svg width="1em" height="1em" viewBox="0 0 34 34" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ cursor: 'not-allowed', opacity: 0.4 }}>
        <path d="M0 6C0 2.68629 2.68629 0 6 0H28C31.3137 0 34 2.68629 34 6V28C34 31.3137 31.3137 34 28 34H6C2.68629 34 0 31.3137 0 28V6Z" fill="#94A3B8" fillOpacity="0.12" />
        <path d="M6 0.5H28C31.0376 0.5 33.5 2.96243 33.5 6V28C33.5 31.0376 31.0376 33.5 28 33.5H6C2.96243 33.5 0.5 31.0376 0.5 28V6C0.5 2.96243 2.96243 0.5 6 0.5Z" stroke="#94A3B8" strokeOpacity="0.4" />
        <path d="M10.1582 14.0424V18.5266C10.1582 19.8003 11.1898 20.8319 12.4635 20.8319H19.1582C19.6319 20.8319 20.0214 20.4424 20.0214 19.9687V15.474C20.0214 14.2003 18.9898 13.1687 17.7161 13.1687H11.0214C10.5477 13.1687 10.1582 13.5477 10.1582 14.0108V14.0424ZM20.7898 19.0319L22.6424 20.6529C22.9372 20.9161 23.4003 20.8845 23.6635 20.5898C23.7793 20.4529 23.8424 20.2845 23.8424 20.1161V13.8845C23.8424 13.4845 23.5161 13.1687 23.1266 13.1582C22.9477 13.1582 22.7793 13.2214 22.6529 13.3372L20.7898 14.9687C20.6319 15.1056 20.5477 15.3056 20.5477 15.5161V18.495C20.5477 18.695 20.6424 18.895 20.7898 19.0319Z" fill="#94A3B8" />
      </svg>
      <span style={{
        position: 'absolute', top: -6, right: -6,
        background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
        color: '#fff', fontSize: 7, fontWeight: 700, letterSpacing: '0.3px',
        padding: '1px 4px', borderRadius: 99, whiteSpace: 'nowrap',
        pointerEvents: 'none', lineHeight: 1.5,
      }}>Soon</span>
    </div>
  );
  const navigation = useNavigate();
  const state = useSelector((state: RootState) => state.auth);
  const isDomainVerified = state.verified_domain;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>

      {/* ── Header ── */}
      <div className='Selected_user_details'>
        <div className='d-flex gap-3'>
          <div
            className='d-flex gap-3 align-items-center'
            onClick={handleProfileNavigation}
            style={{
              cursor: (!isProfileBlocked && isChatConnected) ? 'pointer' : 'default',
              borderRadius: 10,
              padding: '4px 8px 4px 4px',
              transition: 'background 0.15s',
            }}
            onMouseEnter={(e) => { if (!isProfileBlocked && isChatConnected) (e.currentTarget as HTMLDivElement).style.background = '#f0f4ff'; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.background = 'transparent'; }}
          >
            <div className="selected_user_image d-flex align-items-center justify-content-center bg-light text-secondary rounded-circle" style={{ width: 60, height: 60, position: 'relative' }}>
              <img
                src={isBlocked ? NoProfile : (otherUserImage || NoProfile)}
                alt={otherUserName}
                className="w-100 h-100 rounded-circle"
                style={{ objectFit: 'cover' }}
                onError={(e) => { (e.currentTarget as HTMLImageElement).src = NoProfile; }}
              />
              {otherUserOnline && !isBlocked && isChatConnected && (
                <GoDotFill className="inchat_userOnline" />
              )}
            </div>
            <div className='selected_user_deatils'>
              <h3>{otherUserName}</h3>
              {!isBlocked && isChatConnected && (
                <p>{otherUserOnline ? 'Online' : 'Offline'}</p>
              )}
              {!isChatConnected && (
                <p>Unmatched</p>
              )}
              {jobTitle && (
                <div style={{ marginTop: 4, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2" ry="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></svg>
                  <span style={{ fontSize: 12, color: '#64748B', fontWeight: 500 }}>{jobTitle}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className='selected_user_icon'>
          {onFeedback && isChatConnected && <FeedbackSvg />}
          {onVideoCall && isChatConnected && <VideoSvg />}
          <Dropdown>
            <Dropdown.Toggle className='ChatMore_Feature_Btn' variant="none" id="cb-more-dropdown">
              <svg width="1em" height="1em" viewBox="0 0 34 34" fill="none">
                <path d="M0 6C0 2.68629 2.68629 0 6 0H28C31.3137 0 34 2.68629 34 6V28C34 31.3137 31.3137 34 28 34H6C2.68629 34 0 31.3137 0 28V6Z" fill="#457EFF" fillOpacity="0.06" />
                <path d="M6 0.5H28C31.0376 0.5 33.5 2.96243 33.5 6V28C33.5 31.0376 31.0376 33.5 28 33.5H6C2.96243 33.5 0.5 31.0376 0.5 28V6C0.5 2.96243 2.96243 0.5 6 0.5Z" stroke="#457EFF" strokeOpacity="0.6" />
                <path d="M15.5 17C15.5 17.827 16.173 18.5 17 18.5C17.827 18.5 18.5 17.827 18.5 17C18.5 16.173 17.827 15.5 17 15.5C16.173 15.5 15.5 16.173 15.5 17ZM15.5 22C15.5 22.827 16.173 23.5 17 23.5C17.827 23.5 18.5 22.827 18.5 22C18.5 21.173 17.827 20.5 17 20.5C16.173 20.5 15.5 21.173 15.5 22ZM15.5 12C15.5 12.827 16.173 13.5 17 13.5C17.827 13.5 18.5 12.827 18.5 12C18.5 11.173 17.827 10.5 17 10.5C16.173 10.5 15.5 11.173 15.5 12Z" fill="#457EFF" />
              </svg>
            </Dropdown.Toggle>
            <Dropdown.Menu className='Chat_More_Features cb-actions-menu' align="end">
              {(!hasBlocked && isChatConnected && onUnmatch) && (
                <Dropdown.Item as="button" className="cb-actions-menu__item cb-actions-menu__item--danger" onClick={onUnmatch}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" /><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" /><line x1="2" y1="2" x2="22" y2="22" /></svg>
                  Un-Match
                </Dropdown.Item>
              )}
              {onBlock && (
                <Dropdown.Item
                  as="button"
                  className={`cb-actions-menu__item ${hasBlocked ? 'cb-actions-menu__item--primary' : 'cb-actions-menu__item--danger'}`}
                  onClick={onBlock}
                >
                  <MdDoNotDisturbAlt />
                  {hasBlocked ? 'Unblock' : 'Block'}
                </Dropdown.Item>
              )}
              {onReport && (
                blockStatus?.lastReportedDate ? (
                  <div
                    className="cb-actions-menu__item"
                    style={{ cursor: "default", color: "#6B7280" }}
                  >
                    <Flag size={14} />
                    <span>Reported: {new Date(blockStatus.lastReportedDate).toLocaleDateString()}</span>
                  </div>
                  // <div className="cb-actions-menu__item cb-actions-menu__item--info" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '8px 16px', fontSize: '14px', color: '#666', cursor: 'default' }}>
                  //   <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></svg>
                  //   <span>Reported: {new Date(blockStatus.lastReportedDate).toLocaleDateString()}</span>
                  // </div>
                ) : (
                  <Dropdown.Item as="button" className="cb-actions-menu__item cb-actions-menu__item--warn" onClick={onReport}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" /><line x1="4" y1="22" x2="4" y2="15" /></svg>
                    Report
                  </Dropdown.Item>
                )
              )}
              <div className="cb-actions-menu__divider" />
              <Dropdown.Item as="button" className="cb-actions-menu__item">
                <IoClose />
                Cancel
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </div>      </div>

      {/* ── Typing indicator ── */}
      {otherUserTyping && !isBlocked && !hasBlocked && (
        <div className="typing_indicator_bar" style={{
          padding: '2px 20px',
          fontSize: 12,
          color: '#457EFF',
          fontWeight: 500,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          minHeight: 22,
        }}>
          <div className="typing_dots" style={{ display: 'flex', gap: 3 }}>
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: '#457EFF',
              animation: 'typingBounce 1.4s ease-in-out infinite',
            }} />
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: '#457EFF',
              animation: 'typingBounce 1.4s ease-in-out 0.2s infinite',
            }} />
            <span style={{
              width: 6, height: 6, borderRadius: '50%', background: '#457EFF',
              animation: 'typingBounce 1.4s ease-in-out 0.4s infinite',
            }} />
          </div>
          <span>{otherUserName} is typing...</span>
        </div>
      )}

      {/* ── Messages area ── */}
      <div
        className='Chat_Area_For_Selected_User'
      >
        <div
          className={`full_chat_container${messages.length === 0 ? ' full_chat_container--empty' : ''}`}
          ref={chatContainerRef}
          style={{
            opacity: (isChatConnected && isDomainVerified) ? 1 : 0.55,
            filter: (isChatConnected && isDomainVerified) ? 'none' : 'grayscale(20%)',
            transition: 'all 0.2s ease-in-out'
          }}
        >
          {isMessagesLoading ? (
            <div className="d-flex flex-column gap-3 p-3 h-100" style={{ minHeight: '260px' }}>
              <div className="d-flex align-items-end gap-2">
                <Skeleton circle width={36} height={36} style={{ flexShrink: 0 }} />
                <Skeleton width={180} height={38} borderRadius={14} />
              </div>
              <div className="d-flex justify-content-end">
                <Skeleton width={140} height={38} borderRadius={14} />
              </div>
              <div className="d-flex align-items-end gap-2">
                <Skeleton circle width={36} height={36} style={{ flexShrink: 0 }} />
                <Skeleton width={220} height={42} borderRadius={14} />
              </div>
              <div className="d-flex justify-content-end">
                <Skeleton width={170} height={38} borderRadius={14} />
              </div>
            </div>
          ) : messages.length > 0 
          ? messages.map((item, index) => {
            const isOtherUser = String(item.sender_id) !== String(currentUserId);
            const prevItem = index > 0 ? messages[index - 1] : null;
            const prevTime = prevItem?.timestamp || prevItem?.time;
            const currTime = item.timestamp || item.time;
            const showDateSeparator = index === 0 || isDifferentDay(prevTime, currTime);
            const messageText = item.decryptMsg || item.text || '';
            const messageKey = item.chat_id || String(index);
            const isMessageExpanded = Boolean(expandedMessages[messageKey]);
            const getSmartTruncatedText = (rawText: string, limit: number = MESSAGE_PREVIEW_CHAR_LIMIT): string => {
              if (rawText.length <= limit) return rawText;
              let truncated = rawText.slice(0, limit);
              const lastSpaceIndex = truncated.lastIndexOf(' ');
              if (lastSpaceIndex > 130) {
                truncated = truncated.slice(0, lastSpaceIndex);
              }
              return truncated.trimEnd() + '...';
            };

            const renderExpandableText = (text: string, key: string, isOther: boolean = true) => {
              const isLong = text.length > MESSAGE_PREVIEW_CHAR_LIMIT;
              const expanded = Boolean(expandedMessages[key]);
              const displayText = isLong && !expanded ? getSmartTruncatedText(text) : text;

              return (
                <>
                  <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{displayText}</span>
                  {isLong && (
                    <button
                      type="button"
                      className={`chat_message_read_more ${isOther ? 'chat_read_more_other' : 'chat_read_more_self'}`}
                      aria-expanded={expanded}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedMessages((current) => ({ ...current, [key]: !current[key] }));
                      }}
                    >
                      {expanded ? 'Read less' : 'Read more'}
                    </button>
                  )}
                </>
              );
            };
            const messageContent = renderExpandableText(messageText, messageKey, isOtherUser);
            const replyContent = renderExpandableText(item.replyMsgDecrypt || '', `${messageKey}-reply`, isOtherUser);
            return (
              <div key={item.chat_id || index}>
                {showDateSeparator && getDateSeparator(currTime)}
                {isOtherUser ? (
                  <div className='Chats_with_user'>
                    <div className='Chats_with_user_img'>
                      <img src={isBlocked ? NoProfile : (otherUserImage || NoProfile)} alt={item.sender_name} onError={(e) => { (e.currentTarget as HTMLImageElement).src = NoProfile; }} />
                    </div>
                    <div className='Chats_with_user_chat'>
                      <h3>{item.sender_name} <span><GoDotFill /> {formatMessageTime(item.timestamp || item.time)}</span> <PiChecksBold className='messageChecked' /></h3>
                      {item.type === 'text' && item.isReply ? (
                        <div className='User_Reply_chat_history'>
                          <label className='User_chat_Replyed_message'>{messageContent}</label>
                          <div className='User_Replty_to_chat'><div className='User_Replty_to_chat_inner'>
                            <label>{item.sender_id === currentUserId ? 'You' : item.parentMessage?.sender_name}</label>
                            <p>{replyContent}</p>
                          </div></div>
                        </div>
                      ) : (item.type === 'text' || !item.type) && !item.isReply ? (
                        <p className='chat_user_message' style={{ padding: '10px', minHeight: '30px' }}>{messageContent}</p>
                      ) : item.type === 'delete' ? (
                        <p className='chat_deleted_by_user'>This message is deleted by {otherUserName}</p>
                      ) : item.type === 'image' ? (
                        <div style={{ maxWidth: 220 }}>
                          <img src={item.decryptPicUrl || item.pic_url} alt='Image' className='chat_user_upload_image' style={{ width: '100%', borderRadius: 8, cursor: 'pointer' }} onClick={() => window.open(item.decryptPicUrl || item.pic_url, '_blank')} />
                          {Boolean(item.decryptMsg || item.text) && (item.decryptMsg || item.text) !== 'Send an Image' && (
                            <p className='chat_user_message mt-1 mb-0' style={{ padding: '6px 10px', fontSize: 13, wordBreak: 'break-word' }}>
                              {messageContent}
                            </p>
                          )}
                        </div>
                      ) : item.type === 'audio' ? (
                        <VoiceAudioPlayer
                          url={item.decryptPicUrl || item.pic_url || (item as any).url || (item as any).file_url || (item as any).audio_url || (item as any).media_url || ''}
                          duration={item.duration}
                          isSelf={false}
                        />
                      ) : item.type === 'file' ? (
                        <a href={item.decryptPicUrl || item.pic_url} target='_blank' rel='noreferrer' className='chat_file_attachment'>📎 {item.text || 'File'}</a>
                      ) : item.type === 'call' ? (
                        <div className="chat_call_notification" style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 12px', borderRadius: 8,
                          background: 'rgba(69,126,255,0.06)',
                          fontSize: 13, color: '#334155',
                        }}>
                          <IoVideocam style={{ color: item.call_status === 'call_end' ? '#94A3B8' : '#457EFF', fontSize: 18 }} />
                          <span>
                            {item.call_status === 'call_end'
                              ? `Video call ended${item.call_duration ? ` (${item.call_duration})` : ''}`
                              : item.call_status === 'joined'
                                ? 'Video call connected'
                                : 'Incoming video call...'
                            }
                          </span>
                        </div>
                      ) : null}
                      <div className='user_Chat_edit_feature' style={{ display: (item.type === 'delete' || !isChatConnected) ? 'none' : undefined }}>
                        <div className='d-flex align-items-center gap-3'>
                          <div onClick={() => copyMsg(item.decryptMsg || '')}><CopyIcon /></div>
                          <div onClick={() => replyMsg(item.decryptMsg || '', otherUserName, item.chat_id)}><ReplyIcon /></div>
                        </div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className='Chats_with_self'>
                    <div className='Chats_with_self_chat'>
                      <h3>You <span><GoDotFill /> {formatMessageTime(item.timestamp || item.time)}</span> <PiChecksBold className='messageChecked' /></h3>
                      {item.type === 'delete' ? (
                        <p className='chat_deleted_by_user'>You deleted this message</p>
                      ) : item.type === 'image' ? (
                        <div style={{ maxWidth: 220 }}>
                          <img src={item.decryptPicUrl || item.pic_url} alt='Image' className='chat_user_upload_image' style={{ width: '100%', borderRadius: 8, cursor: 'pointer' }} onClick={() => window.open(item.decryptPicUrl || item.pic_url, '_blank')} />
                          {Boolean(item.decryptMsg || item.text) && (item.decryptMsg || item.text) !== 'Send an Image' && (
                            <p className='Chats_self_message mt-1 mb-0' style={{ backgroundColor: '#457eff', color: 'white', padding: '6px 10px', fontSize: 13, wordBreak: 'break-word' }}>
                              {messageContent}
                            </p>
                          )}
                        </div>
                      ) : item.type === 'audio' ? (
                        <VoiceAudioPlayer
                          url={item.decryptPicUrl || item.pic_url || (item as any).url || (item as any).file_url || (item as any).audio_url || (item as any).media_url || ''}
                          duration={item.duration}
                          isSelf={true}
                        />
                      ) : item.type === 'file' ? (
                        <a href={item.decryptPicUrl || item.pic_url} target='_blank' rel='noreferrer' className='chat_file_attachment'>📎 {item.text || 'File'}</a>
                      ) : item.type === 'text' && item.isReply ? (
                        <div className='Reply_chat_history'>
                          <label className='chat_Replyed_message'>{item.decryptMsg}</label>
                          <div className='Replty_to_chat'><div className='Replty_to_chat_inner'>
                            <label>{item.sender_id === currentUserId ? 'You' : item.parentMessage?.sender_name}</label>
                            <p>{replyContent}</p>
                          </div></div>
                        </div>
                      ) : item.type === 'call' ? (
                        <div className="chat_call_notification" style={{
                          display: 'flex', alignItems: 'center', gap: 10,
                          padding: '8px 12px', borderRadius: 8,
                          background: 'rgba(69,126,255,0.06)',
                          fontSize: 13, color: '#334155',
                        }}>
                          <IoVideocam style={{ color: item.call_status === 'call_end' ? '#94A3B8' : '#457EFF', fontSize: 18 }} />
                          <span>
                            {item.call_status === 'call_end'
                              ? `Video call ended${item.call_duration ? ` (${item.call_duration})` : ''}`
                              : item.call_status === 'joined'
                                ? 'Video call connected'
                                : 'Outgoing video call...'
                            }
                          </span>
                        </div>
                      ) : (
                        <p className='Chats_self_message' style={{ backgroundColor: '#457eff', color: 'white', padding: '10px', minHeight: '30px' }}>
                          {messageContent}
                        </p>
                      )}
                      <div className='Chat_Edit_Features' style={{ display: (item.type === 'delete' || !isChatConnected) ? 'none' : undefined }}>
                        <div className='d-flex align-items-center gap-3'>
                          <div onClick={() => copyMsg(item.decryptMsg || '')}><CopyIcon /></div>
                          <div onClick={() => deleteMsg(item.chat_id)}><DeleteIcon /></div>
                          <div onClick={() => replyMsg(item.decryptMsg || '', 'You', item.chat_id)}><ReplyIcon /></div>
                          <span className='EditFeature_devider'>|</span>
                          <div onClick={() => editMsg(item.decryptMsg || '', item.chat_id)}><EditIcon /></div>
                        </div>
                      </div>
                      {item.idEdited && <p className='chat_edited_label'>Edited</p>}
                    </div>
                    <div className='Chats_with_self_img'>
                      <img src={currentUserImage || NoProfile} alt={item.sender_name} onError={(e) => { (e.currentTarget as HTMLImageElement).src = NoProfile; }} />
                    </div>
                  </div>
                )}
              </div>
            );
          }) : (
            <div className='cb_welcome_wrap'>
              <div className='cb_welcome_container'>
                <div className='cb_welcome_icon'>
                  <div className='cb_welcome_logo_wrapper'>
                    <img src={FavLogo} alt='techNbuddy' className='cb_welcome_logo_img' />
                    <span className='cb_welcome_logo_badge'>
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6L4.5 8.5L10 3.5" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" /></svg>
                    </span>
                  </div>
                </div>
                <p className='cb_welcome_label'>TECHNBUDDY CHAT</p>
                <h3 className='cb_welcome_heading'>Welcome to techNbuddy!</h3>
                <p className='cb_welcome_subtitle'>Your professional networking space. Connect, collaborate,<br />and grow with top talent.</p>

                <div className='cb_guidelines_container'>
                  <div className='cb_guideline_item cb_guideline_blue'>
                    <div className='cb_guideline_icon'>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20ZM11 7H13V13H11V7ZM11 15H13V17H11V15Z" fill="#457EFF" /></svg>
                    </div>
                    <div className='cb_guideline_text'>
                      <h4>Professional conduct only</h4>
                      <p>Keep all conversations respectful and appropriate.</p>
                    </div>
                  </div>

                  <div className='cb_guideline_item cb_guideline_green'>
                    <div className='cb_guideline_icon'>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M20 2H4C2.9 2 2 2.9 2 4V22L6 18H20C21.1 18 22 17.1 22 16V4C22 2.9 21.1 2 20 2ZM20 16H6L4 18V4H20V16Z" fill="#22C55E" /><path d="M11 14H13V16H11V14ZM11 6H13V12H11V6Z" fill="#22C55E" /></svg>
                    </div>
                    <div className='cb_guideline_text'>
                      <h4>No inappropriate content</h4>
                      <p>Do not send offensive messages or media.</p>
                    </div>
                  </div>

                  <div className='cb_guideline_item cb_guideline_yellow'>
                    <div className='cb_guideline_icon'>
                      <svg width="22" height="22" viewBox="0 0 24 24" fill="none"><path d="M16 11C17.66 11 18.99 9.66 18.99 8C18.99 6.34 17.66 5 16 5C14.34 5 13 6.34 13 8C13 9.66 14.34 11 16 11ZM8 11C9.66 11 10.99 9.66 10.99 8C10.99 6.34 9.66 5 8 5C6.34 5 5 6.34 5 8C5 9.66 6.34 11 8 11ZM8 13C5.67 13 1 14.17 1 16.5V19H15V16.5C15 14.17 10.33 13 8 13ZM16 13C15.71 13 15.38 13.02 15.03 13.05C16.19 13.89 17 15.02 17 16.5V19H23V16.5C23 14.17 18.33 13 16 13Z" fill="#F59E0B" /></svg>
                    </div>
                    <div className='cb_guideline_text'>
                      <h4>Be a good professional</h4>
                      <p>Build meaningful, constructive connections.</p>
                    </div>
                  </div>
                </div>

                <div className='cb_welcome_footer'>
                  <span>🤝</span>
                  <span className='cb_welcome_footer_text'>Happy networking!</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Input area ── always show unless unmatched or blocked ── */}
        {!isChatConnected ? (
          <div className="unmatched-connect-banner" style={{
            background: 'rgba(255, 255, 255, 0.85)',
            backdropFilter: 'blur(8px)',
            borderTop: '1px solid rgba(69, 126, 255, 0.1)',
            padding: '24px 20px',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '12px',
            width: '100%',
            zIndex: 10
          }}>
            <p style={{
              margin: 0,
              color: '#475569',
              fontSize: '15px',
              fontWeight: 500,
              lineHeight: '1.5'
            }}>
              You are unmatched with this user. Connect to start chatting again.
            </p>
            {/* <button
              onClick={() => onConnect?.()}
              style={{
                background: 'linear-gradient(135deg, #457EFF, #2563EB)',
                color: '#ffffff',
                border: 'none',
                borderRadius: '24px',
                padding: '10px 28px',
                fontSize: '15px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 12px rgba(69, 126, 255, 0.25)',
                transition: 'all 0.2s ease-in-out',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(69, 126, 255, 0.35)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(69, 126, 255, 0.25)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
              </svg>
              Connect
            </button> */}
          </div>
        ) : hasBlocked ? (
          <div className='text-center d-flex flex-column align-items-center justify-content-center gap-2' style={{ minHeight: '80px', padding: '16px', background: '#F8FAFC', borderTop: '1px solid #E2E8F0', width: '100%' }}>              <p style={{ margin: 0, fontSize: '14px', color: '#64748B', fontWeight: 500 }}>You have blocked this user</p>
            <button
              onClick={onBlock}
              style={{
                background: 'linear-gradient(135deg, #EF4444, #DC2626)',
                color: 'white',
                border: 'none',
                borderRadius: '20px',
                padding: '6px 20px',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                boxShadow: '0 4px 6px -1px rgba(239, 68, 68, 0.2)',
                transition: 'all 0.15s ease-in-out'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 6px 8px -1px rgba(239, 68, 68, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'none';
                e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(239, 68, 68, 0.2)';
              }}
            >
              Unblock
            </button>
          </div>
        ) : isDomainVerified ?(
          <div className='d-flex gap-3 w-100 align-items-end chat_composer_row'>
            <div className='send_a_message_to_user chat_composer_input'>
              {editMessageId && (
                <div className='ChatEditMessage_Preview'>
                  <div className='ChatEditMessage_Preview_Inner'>
                    <label>Edit Message <IoClose className='RemoveEdit_Message' onClick={() => { setMessage(''); setPreviewEditMessage(''); setEditMessageId(''); }} /></label>
                    <p>{previewEditMessage}</p>
                  </div>
                </div>
              )}
              {previewReply && (
                <div className='ChatEditMessage_Preview'>
                  <div className='ChatEditMessage_Preview_Inner'>
                    <label>{replyTo} <IoClose className='RemoveEdit_Message' onClick={() => { setIsReply(false); setReplyTo(''); setPreviewReply(''); setReplyChatId(''); }} /></label>
                    <p>{previewReply}</p>
                  </div>
                </div>
              )}
              {uploadingImagePreview && (
                <div className='pt-2 px-2 pb-1 ms-1'>
                  <div 
                    className='position-relative d-inline-block rounded-3 overflow-hidden border shadow-sm'
                    style={{ width: '82px', height: '82px', backgroundColor: '#0f172a' }}
                  >
                    <img
                      src={uploadingImagePreview}
                      alt='Selected attachment'
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        opacity: isUploading ? 0.65 : 1,
                        filter: isUploading ? 'blur(0.5px)' : 'none',
                      }}
                    />

                    {!isUploading && (
                      <button
                        type='button'
                        className='position-absolute top-0 end-0 btn btn-sm p-0 m-1 rounded-circle d-flex align-items-center justify-content-center'
                        style={{
                          width: '22px',
                          height: '22px',
                          backgroundColor: 'rgba(0,0,0,0.65)',
                          color: '#fff',
                          border: 'none',
                          lineHeight: 1,
                          cursor: 'pointer',
                          zIndex: 5,
                        }}
                        onClick={() => {
                          setStagedFile(null);
                          setUploadedUrl(null);
                          uploadPromiseRef.current = null;
                          setUploadingImagePreview((prevUrl) => {
                            if (prevUrl) URL.revokeObjectURL(prevUrl);
                            return null;
                          });
                        }}
                        title='Remove image'
                      >
                        <IoClose size={15} />
                      </button>
                    )}

                    {isUploading && (
                      <div
                        className='position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-white'
                        style={{ backgroundColor: 'rgba(15, 23, 42, 0.55)' }}
                      >
                        <Spinner animation='border' size='sm' variant='light' className='mb-1' />
                        <span style={{ fontSize: '10px', fontWeight: 700, letterSpacing: '0.5px' }}>
                          {uploadProgress > 0 ? `${uploadProgress}%` : 'Uploading'}
                        </span>
                      </div>
                    )}

                    {!isUploading && uploadedUrl && (
                      <div
                        className='position-absolute bottom-0 end-0 m-1 px-1 rounded-circle bg-success text-white d-flex align-items-center justify-content-center'
                        style={{ width: '18px', height: '18px', fontSize: '11px', lineHeight: 1 }}
                        title='Image ready'
                      >
                        ✓
                      </div>
                    )}
                  </div>
                </div>
              )}
              <div className='position-relative'>
                <BiSolidSmile className='chat_emoji_icon' onClick={() => setShowEmojiPicker((p) => !p)} />
                {showEmojiPicker && (
                  <div ref={emojiRef} style={{ position: 'absolute', bottom: '60px', left: '10px', zIndex: 1000 }}>
                    <Suspense fallback={<div className='bg-white border rounded p-3 small text-muted'>Loading emojis...</div>}>
                      <EmojiPicker onEmojiClick={handleEmojiClick} width={350} height={400} />
                    </Suspense>
                  </div>
                )}
                <textarea
                  ref={textareaRef}
                  rows={1}
                  className='no-focus-style cb-chat-textarea'
                  placeholder={stagedFile ? 'Add a caption...' : 'Type your message here...'}
                  value={message}
                  onChange={handleInputChange}
                  onKeyDown={handleKeyDown}
                  style={{ display: isRecording ? 'none' : undefined }}
                  disabled={isUploading}
                />
                {/* Waveform visualiser — shown while recording */}
                {isRecording && (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8, padding: '0 8px' }}>
                    <span style={{ fontSize: 11, color: '#ef4444', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      ● {Math.floor(recordingSeconds / 60).toString().padStart(2, '0')}:{(recordingSeconds % 60).toString().padStart(2, '0')}
                    </span>
                    <canvas
                      ref={waveCanvasRef}
                      width={200}
                      height={32}
                      style={{ flex: 1, maxWidth: 200, borderRadius: 4 }}
                    />
                    <button
                      type='button'
                      onClick={stopRecording}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#ef4444', padding: 0, fontSize: 18, lineHeight: 1 }}
                      title='Cancel recording'
                    >
                      <IoClose />
                    </button>
                  </div>
                )}
                <div className='chat_more_features_icon'>
                  {isUploading && (
                    <span style={{ fontSize: 11, color: '#457EFF', marginRight: 6 }}>{uploadProgress}%</span>
                  )}
                  {!isRecording && (
                    <Dropdown>
                      <Dropdown.Toggle className='ChatMore_Feature_Btn' variant="none" id="cb-attach-dropdown" disabled={isUploading}>
                        <MdAttachFile className='chat_attachment_sec' />
                      </Dropdown.Toggle>
                      <Dropdown.Menu className='Chat_More_Features' align="end">
                        <Dropdown.Item as="div" onClick={() => { if (fileInputRef.current) { fileInputRef.current.accept = 'image/jpeg,image/png,image/gif,image/webp'; fileInputRef.current.click(); } }}><HiCamera /> Image</Dropdown.Item>
                      </Dropdown.Menu>
                    </Dropdown>
                  )}
                  {/* Audio recording — Coming Soon */}
                  <div
                    style={{
                      position: 'relative', display: 'inline-flex',
                      alignItems: 'center', justifyContent: 'center',
                      cursor: 'not-allowed', opacity: 0.45,
                    }}
                    title="Voice Messages — Coming Soon"
                  >
                    <GiOldMicrophone style={{ fontSize: '1.2em', color: '#94A3B8' }} />
                    <span style={{
                      position: 'absolute', top: -6, right: -8,
                      background: 'linear-gradient(135deg,#f59e0b,#ef4444)',
                      color: '#fff', fontSize: 7, fontWeight: 700, letterSpacing: '0.3px',
                      padding: '1px 4px', borderRadius: 99, whiteSpace: 'nowrap',
                      pointerEvents: 'none', lineHeight: 1.5,
                    }}>Soon</span>
                  </div>
                  <input
                    ref={fileInputRef}
                    type='file'
                    accept='image/jpeg,image/png,image/gif,image/webp'
                    style={{ display: 'none' }}
                    onChange={handleFileSelect}
                  />
                </div>
              </div>
            </div>
            <div className='chat_send_message chat_composer_send'>
              <button
                onClick={isRecording ? stopRecording : submitChat}
                disabled={isUploading || (!message.trim() && !stagedFile && !isRecording)}
                title={isRecording ? 'Stop & send voice message' : 'Send message'}
                style={isRecording ? { background: '#ef4444' } : undefined}
              >
                <RiSendPlaneFill />
              </button>
            </div>
          </div>
          
        ): (<div className="verify-banner mb-4 rounded-0">
            <span className="verify-banner__content">
              <svg
                className="verify-banner__icon"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
              <span>
                Your account is not verified. Please verify your account to unlock all features.
              </span>
            </span>

            <button
              type="button"
              className="verify-banner__action"
              onClick={() => { navigation('/business-verification') }}
            >
              Verify Now
            </button>
          </div>)}
      </div>
    </div>
  );
};

export default ChatBoard;
